import React, { FC, useMemo, useState } from 'react';
import { Button, Form, Row, Steps } from 'antd';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { budgetsRestCrudThunksForBuyer } from '../../thunks/budget_thunks';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getDateOnlyFromMomentToBEFormat } from '../../utils/DataFormatterUtils';
import { allCyclesCustomValidator } from './BudgetVariablePeriodCycleField';
import BudgetFirstSteps from './BudgetFirstSteps';
import BudgetSecondSteps from './BudgetSecondSteps';
import BudgetThirdSteps, { BUDGET_TYPE } from './BudgetThirdSteps';
import { transformBudgetAllCycles } from '../../utils/BudgetUtils';

const STEP_NAMES = {
	first: ['title', 'periodStartDate'],
	second: ['accrualTypesTracked'],
};

const BudgetForm: FC<any> = ({
	formData,
	currentUser,
	onSuccess,

	createBudgetForm,
	updateBudgetForm,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const [buttonLoading, setButtonLoading] = useState(false);
	const [locationDistinctBudget, setLocationDistinctBudget] = useState([]);

	const [step, setStep] = useState(0);
	const currency = getCurrency({ currentUser });

	const budgetShareType = Form.useWatch('budgetShareType', form);
	const companyWideBudgetShareType = Form.useWatch('companyWideBudgetShareType', form);
	const months = Form.useWatch('months', form);

	const isBudgetDistinctForEachLoc = useMemo(
		() => budgetShareType === BUDGET_TYPE.distinct,
		[budgetShareType]
	);

	const budgetShareTypeIsForEachPeriod = useMemo(
		() => budgetShareType === BUDGET_TYPE.sharedForEachPeriod,
		[budgetShareType]
	);

	const companyWideBudgetShareTypeIsForEachPeriod = useMemo(
		() => companyWideBudgetShareType === BUDGET_TYPE.sharedForEachPeriod,
		[companyWideBudgetShareType]
	);

	const isBudgetSharedForEachPeriod = useMemo(
		() => budgetShareTypeIsForEachPeriod || companyWideBudgetShareTypeIsForEachPeriod,
		[budgetShareTypeIsForEachPeriod, companyWideBudgetShareTypeIsForEachPeriod]
	);

	const handleSubmit = (values) => {
		form.validateFields(['sharedBudget']).then(() => {
			const submit = values.id ? updateBudgetForm : createBudgetForm;

			const variablePeriod = values.months === -1;

			const defaultBudgetShareValues = {
				sharedBudget: undefined,
				budgetPerPeriod: undefined,
				budgetPerLocations: undefined,
				locationId: undefined,
			};

			const budget = {
				id: values.id,
				title: values.title,
				description: values.description,
				startDate: getDateOnlyFromMomentToBEFormat(nullSafeGet('periodStartDate', values)),
				endDate: nullSafeGet('periodEndDate', values)
					? getDateOnlyFromMomentToBEFormat(nullSafeGet('periodEndDate', values), true)
					: undefined,
				months: !variablePeriod ? values.months : undefined,
				allCycles: variablePeriod ? transformBudgetAllCycles(values.allCycles) : undefined,
				recurring: values.recurring,
				...(isBudgetDistinctForEachLoc
					? {
							...defaultBudgetShareValues,
							budgetPerLocations: locationDistinctBudget.map((loc) => ({
								locationId: parseInt(loc.id),
								budget: parseFloat(loc.budget),
							})),
					  }
					: isBudgetSharedForEachPeriod
					? {
							...defaultBudgetShareValues,
							budgetPerPeriod: (values.budgetPerPeriod || []).map((bpc) => ({
								cycle: bpc.cycle,
								budget: bpc.budget,
							})),
							//Optional
							locationIds: companyWideBudgetShareTypeIsForEachPeriod
								? undefined
								: values.locationIds,
					  }
					: {
							...defaultBudgetShareValues,
							sharedBudget: values.sharedBudget,
							//Optional
							locationIds: values.locationIds,
					  }),
				thresholdPercentage: values.thresholdPercentage.toString(),
				accrualTypesTracked: values.accrualTypesTracked,

				isCompanyWide: values.isCompanyWide,
				problemTypeIds:
					nullSafeGetOrElse('problemTypeIds', values, []).length > 0
						? values.problemTypeIds
						: undefined,
				supplierFacilityIds:
					nullSafeGetOrElse('supplierFacilityIds', values, []).length > 0
						? values.supplierFacilityIds
						: undefined,
				isCapex: values.isCapex,
				isPM: values.isPM,
				trackOnlyCompletedWorkOrders: values.trackOnlyCompletedWorkOrders,
				avgAccrualAmountPerWorkOrder: values.avgAccrualAmountPerWorkOrder,
			};

			setButtonLoading(true);
			submit(budget)
				.then((result) => {
					setButtonLoading(false);
					onSuccess(result);
				})
				.catch((e) => {
					console.log(e);
					console.log('error');
				});
		});
	};

	const onFirstNext = async () => {
		//Validate allCycle only if recurring and variable periods
		const hasCycleErrors = months === -1 ? allCyclesCustomValidator(form) : false;

		try {
			await form.validateFields(STEP_NAMES.first);
			if (!hasCycleErrors) {
				setStep(1);
			}
		} catch (antdErrors) {
			console.log('Other field errors:', antdErrors);
		}
	};

	const onSecondNext = () => {
		form
			.validateFields(STEP_NAMES.second)
			.then(() => {
				setStep(2);
			})
			.catch(console.log);
	};

	const isUpdate = useMemo(() => !!formData.id, [formData.id]);

	const submitLabel = useMemo(() => `${isUpdate ? 'Update' : 'Create'} Budget`, []);

	const isPeriodBasedFormData = useMemo(
		() => isUpdate && formData.recurring && !formData.months,
		[formData.months, formData.recurring, isUpdate]
	);

	const monthsInitialValue = useMemo(
		() => (isPeriodBasedFormData ? -1 : formData.months || 1),
		[formData.months, isPeriodBasedFormData]
	);

	return (
		<Form
			style={{ maxWidth: 600 }}
			layout="vertical"
			form={form}
			preserve={false}
			initialValues={{
				...formData,
				months: monthsInitialValue,
			}}
			onFinish={handleSubmit}
		>
			<Row className={'mb-4'}>
				<Steps current={step}>
					<Steps.Step title="Basic Information" />
					<Steps.Step title="Filters" />
					<Steps.Step title="Locations" />
				</Steps>
			</Row>
			<Form.Item name="id" initialValue={formData.id} hidden={true} />
			<BudgetFirstSteps step={step} formData={formData} form={form} currency={currency} />
			<BudgetSecondSteps step={step} formData={formData} form={form} />

			<BudgetThirdSteps
				step={step}
				formData={formData}
				form={form}
				currency={currency}
				locationDistinctBudget={locationDistinctBudget}
				setLocationDistinctBudget={setLocationDistinctBudget}
			/>

			<Row className={'gap-2'}>
				<Button
					hidden={step !== 0}
					type="primary"
					loading={buttonLoading}
					className="contactForm__button"
					onClick={onFirstNext}
				>
					Next
				</Button>
				<Button
					loading={buttonLoading}
					className="contactForm__button"
					onClick={() => setStep(0)}
					hidden={step !== 1}
				>
					Back
				</Button>
				<Button
					type="primary"
					loading={buttonLoading}
					className="contactForm__button"
					onClick={onSecondNext}
					hidden={step !== 1}
				>
					Next
				</Button>
				<Button
					hidden={step !== 2}
					loading={buttonLoading}
					className="contactForm__button"
					onClick={() => setStep(1)}
				>
					Back
				</Button>
				<Button
					hidden={step !== 2}
					type="primary"
					htmlType="submit"
					loading={buttonLoading}
					className="contactForm__button"
				>
					{submitLabel}
				</Button>
			</Row>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	createBudgetForm: (budget) => dispatch(budgetsRestCrudThunksForBuyer.create(budget)),
	updateBudgetForm: (budget) => dispatch(budgetsRestCrudThunksForBuyer.update(budget)),
});

export default withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(BudgetForm));
